import { inIFrame } from '@thoughtspot/browser-util';
import {
    getClientLocale,
    getPreferredLocaleID,
    initializeDateFormats,
    initializeNumberUtil,
    initializeStrings,
} from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import {
    getEmbedCustomerGuideSrc,
    getEmbedCustomerGuideUIToggle,
    getQuarterStartMonth,
    getSessionTimeZone,
    getUserGuid,
    is1MindBotEnabled,
    isCustomCalendarEnabled,
    isDarkCluster,
    isOptimizelyEnabled,
    isTrialVersionEnabled,
    onThirdPartyServicesInitiated,
} from '@thoughtspot/session-service';
import {
    getMixpanelAccessToken,
    getSessionStorageItem,
    IS_AUTHENTICATION_DONE,
    IS_AUTHENTICATION_DONE_FLAG_TTL,
    isIntercomEnabled,
    isPendoEnabled,
    isPublicUserEnabled,
    REDIRECT_URI,
    setSessionStorageWithExpiry,
} from '@thoughtspot/system-config-service';
import { isWebView } from '/@utils/embed-base.util';

const CONNECTIVITY_REQUEST_TIMEOUT_MS = 500;
const Logger = create('lifecycle-hooks-service');

export const isEmbedded = (path = '') => {
    return inIFrame() || path.indexOf('/embed/') > -1 || isWebView();
};

/**
 * Third party Services
 */
const initPendoServiceAndInsertScript = async () => {
    if (isPendoEnabled()) {
        try {
            const PendoService = await import('/@services/pendo/pendo-service');
            PendoService.initPendoScript({
                getUserPreferredLocale: getPreferredLocaleID,
            });
        } catch (err) {
            Logger.warn(err);
        }
    }
};

const initializeMetrics = async () => {
    try {
        const MetricsService = await import(
            '/@services/metrics-service/metrics-service'
        );
        if (MetricsService.isMixpanelAllowed()) {
            await MetricsService.initMetricsService();
            await MetricsService.initEventCollection();
        } else {
            MetricsService.disableMetricsService();
        }
    } catch (err) {
        Logger.warn(err);
    }
};

const initializeIntercom = async () => {
    // When 1mind bot is enabled, we don't need to initialize intercom
    if (isIntercomEnabled() && !is1MindBotEnabled()) {
        try {
            const IntercomService = await import(
                '/@services/intercom/intercom-service'
            );
            IntercomService.initIntercomScript();
        } catch (err) {
            Logger.warn(err);
        }
    }
};

const initialize1MindBot = async () => {
    if (is1MindBotEnabled() && isTrialVersionEnabled()) {
        try {
            const { init1mindService } = await import(
                '/@services/1mind/1mind-service'
            );
            await init1mindService();
        } catch (err) {
            Logger.warn(err);
        }
    }
};

const initializeObservability = async () => {
    try {
        const ObservabilityService = await import(
            '/@services/system/observability-service/observability-service'
        );
        await ObservabilityService.initializeObservability();
    } catch (err) {
        Logger.error(err);
    }
};

export const initializeEmbedCustomerGuideScript = async () => {
    if (!isEmbedded() || !getEmbedCustomerGuideUIToggle()) {
        return;
    }
    try {
        const {
            setcustomVariablesForThirdPartyTools,
            injectEmbedCustomerGuideScript,
        } = await import('/@utils/embed-base.util');

        setcustomVariablesForThirdPartyTools(Logger);
        injectEmbedCustomerGuideScript(Logger, getEmbedCustomerGuideSrc());
    } catch (err) {
        Logger.warn('Failed to load initialize embedCustomerGuide', err);
    }
};

export const initializeThirdPartyServices = async () => {
    if (isDarkCluster()) {
        Logger.info('Dark cluster detected. Skipping 3rd party services');
        return;
    }
    try {
        await Promise.all([
            initPendoServiceAndInsertScript(),
            initializeMetrics(),
            initializeIntercom(),
            initialize1MindBot(),
            initializeObservability(),
        ]);
        onThirdPartyServicesInitiated.publish();
    } catch (err) {
        Logger.warn('Failed to initialize 3rd party services', err);
    }
};

/**
 * Internal Services
 */

export const initializeCustomCalendars = async () => {
    if (isCustomCalendarEnabled()) {
        try {
            const CustomCalendarService = await import(
                '@thoughtspot/custom-calendar-service'
            );
            CustomCalendarService.initAvailableCustomCalendars();
        } catch (err) {
            Logger.warn(err);
        }
    }
};

export const initializeTooltip = async () => {
    try {
        await import('../../../tooltip-wrapper.module.scss');
        const TooltipWidget = await import(
            '@thoughtspot/radiant-react/widgets/tooltip'
        );
        TooltipWidget.initializeTooltip();
    } catch (err) {
        Logger.warn(err);
    }
};

export const notifySDKOnAuth = async () => {
    if (isEmbedded()) {
        try {
            const { notifyAuthInit } = await import(
                '/@services/everywhere/everywhere-service.util'
            );
            notifyAuthInit(
                getUserGuid(),
                getMixpanelAccessToken(),
                isPublicUserEnabled(),
            );
        } catch (err) {
            Logger.warn(err);
        }
    }
};
export const setEmbedInitData = async (data: any) => {
    if (isEmbedded()) {
        try {
            const { setEmbedInitData } = await import(
                '/@services/everywhere/everywhere-service.util'
            );
            await setEmbedInitData(data);
        } catch (err) {
            Logger.warn(err);
        }
    }
};

export function initializeLocaleFormats(
    dateFormatLocale: string,
    numberFormatLocale: string,
    currencyFormat: string,
): Promise<[void, void, void]> {
    const userLocale = getClientLocale();
    return Promise.all([
        initializeNumberUtil(numberFormatLocale || userLocale, {
            currencyFormat,
        }),
        initializeStrings(userLocale),
        initializeDateFormats(
            getQuarterStartMonth(),
            getSessionTimeZone(),
            dateFormatLocale || userLocale,
        ),
    ]);
}

/**
 * Forces a page reload if an In page OAuth authentication flow has been triggered.
 *
 * This function checks if a redirect URI exists in session storage and whether
 * the authentication process has already been completed. If the authentication
 * process is not marked as completed, it sets a session storage flag with an
 * expiry time and redirects the user to the stored redirect URI.
 *
 * @returns {boolean} True if a redirect was triggered, false otherwise.
 */
export function forceReloadWhenOAuthTriggered(): boolean {
    if (!isEmbedded()) {
        return false;
    }
    const isReloadDone = getSessionStorageItem(IS_AUTHENTICATION_DONE);
    if (getSessionStorageItem(REDIRECT_URI) && isReloadDone !== 'true') {
        setSessionStorageWithExpiry(
            IS_AUTHENTICATION_DONE,
            'true',
            IS_AUTHENTICATION_DONE_FLAG_TTL,
        );
        window.location.href = getSessionStorageItem(REDIRECT_URI);
        return true;
    }
    return false;
}

const stringOverridesUrlKey = 'overrideStringIDsUrl';
let stringOverridesPromise: Promise<any> | null = null;
export const stringOverrideLoader = (stringIdUrl?: string) => {
    // We are using this before env flags are initialized,
    // so we need to use window.location.search
    // eslint-disable-next-line thoughtspot-eslint/ban-window-location-search
    const urlParams = new URLSearchParams(window.location.search);
    const stringsOverrideUrl =
        urlParams.get(stringOverridesUrlKey) || stringIdUrl;
    if (!stringsOverrideUrl) {
        return Promise.resolve({});
    }
    if (!stringOverridesPromise) {
        stringOverridesPromise = fetch(stringsOverrideUrl).then(res =>
            res.json(),
        );
    }
    return stringOverridesPromise;
};

/**
 * Add style overrides needed for exposing the translationIDs.
 */
export const exposeStringsStyleOverrider = (exposeStrings: boolean) => {
    if (!exposeStrings) return;
    const styleDom = document.createElement('style');
    styleDom.id = 'expose-strings-style-overrides';
    styleDom.innerHTML = `
        * {
            text-transform: none !important;
        }
    `;
    document.body.appendChild(styleDom);
};

export const testonly__exports = {
    initializeIntercom,
    initializeMetrics,
    initPendoServiceAndInsertScript,
    initializeObservability,
    initialize1MindBot,
    forceReloadWhenOAuthTriggered,
};
